import { createSlice } from '@reduxjs/toolkit';

/** @typedef {{ user_id: string, login_id?: string, [k: string]: unknown }} User */

/**
 * @typedef {{
 *   loginId: string,       // email or phone the user entered on /signup
 *   licenseKey: string,    // optional license collected at signup
 *   intent: 'signup' | 'otp-login' | 'forgot',
 *   returnPath?: string,   // protected URL to restore after existing-user OTP login
 *   userId?: string | number, // legacy OTP verify requires user_id from /otplogin
 * }} SignupContext
 */

/**
 * @typedef {{
 *   status: 'anonymous' | 'loading' | 'authenticated' | 'error',
 *   token: string | null,
 *   user: User | null,
 *   org: { id: string, name: string } | null,
 *   error: string | null,
 *   otpStatus: 'idle' | 'loading' | 'sent' | 'verifying' | 'error',
 *   otpError: string | null,
 *   forgotStatus: 'idle' | 'loading' | 'sent' | 'error',
 *   forgotError: string | null,
 *   signupContext: SignupContext | null,
 *   ssoStatus: 'idle' | 'validating' | 'error',
 *   ssoError: string | null,
 *   loginSource: 'password' | 'otp' | 'sso' | 'landing-sso' | 'google' | 'facebook' | 'social' | null,
 *   ssoContext: object | null,
 *   onboardingPending: boolean,
 *   onboardingCompleting: boolean,
 *   onboardingError: string | null,
 * }} AuthState
 */

/** @type {AuthState} */
const initialState = {
  status: 'anonymous',
  token: null,
  user: null,
  org: null,
  error: null,
  otpStatus: 'idle',
  otpError: null,
  forgotStatus: 'idle',
  forgotError: null,
  signupContext: null,
  ssoStatus: 'idle',
  ssoError: null,
  loginSource: null,
  ssoContext: null,
  onboardingPending: false,
  onboardingCompleting: false,
  onboardingError: null,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Existing sign-in (password) path
    signInRequested(state) {
      state.status = 'loading';
      state.error = null;
    },
    socialSignInRequested(state) {
      state.status = 'loading';
      state.error = null;
    },
    signInSucceeded(state, action) {
      state.status = 'authenticated';
      state.token = action.payload.token ?? null;
      state.user = action.payload.user ?? null;
      state.org = action.payload.org ?? null;
      state.loginSource = action.payload.loginSource ?? state.loginSource ?? null;
      state.ssoContext = action.payload.ssoContext ?? state.ssoContext ?? null;
      state.error = null;
      state.ssoStatus = 'idle';
      state.ssoError = null;
      // Legacy API: data.onboarding=true means complete, false means pending.
      // The saga converts that into this app's onboardingPending boolean.
      state.onboardingPending = !!action.payload.onboardingPending;
      state.onboardingCompleting = false;
      state.onboardingError = null;
    },
    signInFailed(state, action) {
      state.status = 'error';
      state.error = action.payload ?? 'Sign in failed';
    },
    signedOut() {
      return initialState;
    },
    sessionExpired() {
      return initialState;
    },
    authUserPatched(state, action) {
      if (!state.user) return;
      state.user = {
        ...state.user,
        ...Object.fromEntries(
          Object.entries(action.payload ?? {}).filter(([, value]) => value !== undefined),
        ),
      };
    },

    // OTP request / verify path (used by signup AND existing-user OTP login)
    otpRequested(state, action) {
      state.otpStatus = 'loading';
      state.otpError = null;
      state.signupContext = action.payload;  // { loginId, licenseKey?, intent }
    },
    otpRequestSucceeded(state, action) {
      state.otpStatus = 'sent';
      const userId = action.payload?.userId
        ?? action.payload?.user_id
        ?? action.payload?.login_user_id
        ?? null;
      if (userId !== null && userId !== undefined && state.signupContext) {
        state.signupContext = { ...state.signupContext, userId };
      }
    },
    otpRequestFailed(state, action) {
      state.otpStatus = 'error';
      state.otpError = action.payload ?? 'Could not send code';
    },
    otpVerifyRequested(state) {
      state.otpStatus = 'verifying';
      state.otpError = null;
    },
    otpVerifyFailed(state, action) {
      state.otpStatus = 'error';
      state.otpError = action.payload ?? 'Code did not match';
    },
    signupContextCleared(state) {
      state.signupContext = null;
      state.otpStatus = 'idle';
      state.otpError = null;
    },

    // SSO callback validation
    ssoValidateRequested(state, action) {
      state.status = 'loading';
      state.error = null;
      state.ssoStatus = 'validating';
      state.ssoError = null;
      state.ssoContext = action.payload?.context ?? null;
      state.loginSource = action.payload?.context?.referer === 'landing' ? 'landing-sso' : 'sso';
    },
    ssoValidateFailed(state, action) {
      state.status = 'anonymous';
      state.token = null;
      state.user = null;
      state.org = null;
      state.error = action.payload ?? 'Could not complete SSO sign in.';
      state.ssoStatus = 'error';
      state.ssoError = state.error;
    },
    ssoStateReset(state) {
      state.ssoStatus = 'idle';
      state.ssoError = null;
      state.ssoContext = null;
    },

    // Forgot-password
    forgotRequested(state) {
      state.forgotStatus = 'loading';
      state.forgotError = null;
    },
    forgotSucceeded(state) {
      state.forgotStatus = 'sent';
    },
    forgotFailed(state, action) {
      state.forgotStatus = 'error';
      state.forgotError = action.payload ?? 'Could not send code';
    },
    forgotReset(state) {
      state.forgotStatus = 'idle';
      state.forgotError = null;
    },

    // Onboarding completion
    onboardingCompletionStarted(state) {
      state.onboardingCompleting = true;
      state.onboardingError = null;
    },
    onboardingCompletionFailed(state, action) {
      state.onboardingCompleting = false;
      state.onboardingError = action.payload ?? "We couldn't save that. Please try again.";
    },
    onboardingCompleted(state, action) {
      state.onboardingPending = false;
      state.onboardingCompleting = false;
      state.onboardingError = null;
      if (state.user) {
        state.user = {
          ...state.user,
          ...Object.fromEntries(
            Object.entries(action.payload ?? {}).filter(([, value]) => value !== undefined),
          ),
          onboarding: true,
        };
      }
    },
  },
});

export const {
  signInRequested, socialSignInRequested, signInSucceeded, signInFailed, signedOut, sessionExpired,
  authUserPatched,
  otpRequested, otpRequestSucceeded, otpRequestFailed,
  otpVerifyRequested, otpVerifyFailed,
  signupContextCleared,
  ssoValidateRequested, ssoValidateFailed, ssoStateReset,
  forgotRequested, forgotSucceeded, forgotFailed, forgotReset,
  onboardingCompletionStarted, onboardingCompletionFailed, onboardingCompleted,
} = slice.actions;
export const authReducer = slice.reducer;
